import type {
  KeystoneUser,
  KeystoneUserResponse,
  UiEvents,
} from "@keystone-ui/types";
import type { RoofMarketplaceBidRequest } from "@keystone-ui/types";

import {
  BadRequestError,
  MissingArgumentsError,
  NetworkError,
  NotFoundError,
  SSError,
  UnauthorizedRequestError,
  isError,
} from "data/ss-error";
import { type TingEnrollmentValues } from "pages/offer/ting/hooks";
import { type WindDefenseEnrollmentValues } from "pages/offer/wind-defense/hooks";
import { type EasyPayFormValues } from "pages/payment-plan-update/payment-plan-update";
import { handleUnauthorized } from "utils";

import { captureException } from "../utils/sentry";
import {
  type ClaimConstants,
  type ClaimList,
  type CoverageData,
  type CoveragePreview,
  type CoverageUpsell,
  type DecodeData,
  type KeystoneClaimsData,
  type KeystoneDocusignStatus,
  type KeystoneInspectionAsset,
  type KeystoneInspections,
  type KeystoneInsuredInfoUpdate,
  type KeystoneMortgageeInfo,
  type KeystoneOfferEligibility,
  type KeystonePaymentInfo,
  type KeystonePolicyDetail,
  type KeystonePolicyInfo,
  type KeystonePolicyIdVerification,
  type PaymentsPlans,
  type PolicyDetail,
  type PropertySection,
  type PropertyUpdateItem,
  claimsDataMap,
  policyDetailMap,
} from "./keystone-api-mapping";

type QueryParams = Record<string, string>;

export type KeystoneResult<T> = T | SSError;

type KeystoneSuccess = { success: boolean };

type RoofMarketplaceBidResponse = {
  id: string;
  friendlyId: string;
  _links: { bids: string; self: string };
};

type InspectionUploadResponse = {
  successfulAssets: KeystoneInspectionAsset[];
  failedAssets: {
    error: string;
    fileName: string;
    message: string;
    statusCode: number;
  }[];
};

const NO_EMAIL_ERR_MSG = "no user email provided";
const NO_USER_ID_MSG = "no user id provided";
const NO_POLICY_ID_ERR_MSG = "no policy ID provided";
const NO_FNOL_EVENT_ERR_MSG = "no FNOL event provided";
const NO_ZIP_CODE_ERR_MSG = "no ZIP Code provided";
const NO_ENVELOPE_ID_ERR_MSG = "no envelope ID provided";
const NO_DATA_ERR_MSG = "no encoded data provided";
const NO_PAYMENT_INFO_MSG = "no payment info provided";
const NO_ACCOUNT_PREFERENCES_MSG = "no account preferences provided";
const NO_FIRST_NAME_MSG = "no first name provided";

/**
 * Keystone API
 */
export class KeystoneApiClient {
  private readonly baseUrl: string;

  /**
   * Setup a keystone-api client to make direct requests to the backend.
   * - provide the `baseUrl`` for the appropriate environment, like https://stage-sagesure-svc.icg360.org/cru-4/keystone
   */
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl.startsWith("http")
      ? baseUrl
      : window.location.origin;
  }

  /**
   * Internal implementation for GET requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - an optional query string will be constructed as needed
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  protected readonly get = async <T>(
    endpoint: string,
    query?: QueryParams,
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (query) {
      Object.keys(query).forEach((key) => {
        url.searchParams.append(key, query[key]);
      });
    }
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    try {
      const response = await fetch(url, { headers });
      const parsedRes = await parseResponse<T>(response);
      if (isError(parsedRes)) {
        captureException(parsedRes);
        if (parsedRes instanceof UnauthorizedRequestError) {
          handleUnauthorized({ endpoint });
        }
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for POST requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly post = async <T>(
    endpoint: string | URL,
    body?: BodyInit,
    headers: HeadersInit = {},
    emptyRes = false,
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    try {
      const response = await fetch(url, {
        headers,
        method: "POST",
        body,
      });
      const parsedRes = await parseResponse<T>(response, emptyRes);
      if (isError(parsedRes)) {
        captureException(parsedRes);
        if (parsedRes instanceof UnauthorizedRequestError) {
          handleUnauthorized({ endpoint: endpoint.toString() });
        }
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for PUT requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly put = async <T>(
    endpoint: string | URL,
    body?: BodyInit,
    headers: HeadersInit = {},
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    try {
      const response = await fetch(url, {
        headers,
        method: "PUT",
        body,
      });
      const parsedRes = await parseResponse<T>(response);
      if (isError(parsedRes)) {
        captureException(parsedRes);
        if (parsedRes instanceof UnauthorizedRequestError) {
          handleUnauthorized({ endpoint: endpoint.toString() });
        }
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for PATCH requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly patch = async <T>(
    endpoint: string | URL,
    body?: BodyInit,
    headers: HeadersInit = {},
    emptyRes = false,
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    try {
      const response = await fetch(url, {
        body,
        headers,
        method: "PATCH",
      });
      const parsedRes = await parseResponse<T>(response, emptyRes);
      if (isError(parsedRes)) {
        captureException(parsedRes);
        if (parsedRes instanceof UnauthorizedRequestError) {
          handleUnauthorized({ endpoint: endpoint.toString() });
        }
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  private readonly delete = async (
    endpoint: string | URL,
    headers: HeadersInit = {},
  ): Promise<KeystoneResult<null>> => {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    try {
      const response = await fetch(url, {
        headers,
        method: "DELETE",
      });
      if (!response.ok) {
        const err = new KeystoneServerError("delete failed", response.status);
        captureException(err);
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized({ endpoint: endpoint.toString() });
        }
        return err;
      }
      return null;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Decode an encrypted string of user data
   */
  decodeData = async (
    dataString: string,
  ): Promise<KeystoneResult<DecodeData>> => {
    if (!dataString) {
      return new MissingArgumentsError(NO_DATA_ERR_MSG);
    }
    return this.post<DecodeData>("/api/data/decode", dataString);
  };

  /**
   * Fetch a keystone user by email.
   */
  getUser = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const res = await this.get<KeystoneUserResponse>("/api/user/email", {
      email,
    });
    if (isError(res)) {
      return res;
    }
    const user: KeystoneUser = {
      ...res,
      policyList: res.policyList.map((p) => {
        return {
          keystonePolicyId: p.keystonePolicyId,
          quoteNumber: p.quoteNumber,
          platformPolicyId: p.platformPolicyId,
          policyState: p.policyState,
          carrier: p.carrier,
          carrierId: p.carrierId,
          carrierGroupId: p.carrierGroupId,
          isCommercial: !!p.isCommercial,
          propertyAddress: p.addresses?.find(
            (addr) => addr.addressType === "PROPERTY",
          ),
          agencyLocationCode: p.terms[0]?.agencyLocationCode ?? "",
          policyType: p.terms[0]?.policyType ?? null,
        };
      }),
    };
    return user;
  };

  /**
   * Link a policy to keystone user. Error message is mapped for display.
   */
  linkPolicy = async (email: string, policyNum: string, zip: string) => {
    if (!email || !policyNum || !zip) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);

    const params = new URLSearchParams();
    params.append("zipCode", zip);
    const res = await this.patch<void>(
      `/api/user/${encodedEmail}/policy/${policyNum}?${params.toString()}`,
      undefined,
      { "Content-Type": "application/x-www-form-urlencoded" },
    );

    if (res instanceof SSError) {
      res.message =
        "Sorry, something went wrong while adding your policy. Please try again in a few minutes or call us at (800) 481-0661.";
      if (res instanceof NotFoundError) {
        res.message =
          "We were unable to find a SageSure insurance policy with that combination of policy number and zip code. Please try again.";
      } else if (res instanceof KeystoneServerError && res.statusCode === 409) {
        res.message = "This policy is already linked to your account.";
      }
      return res;
    }

    return res;
  };

  /**
   * Remove a linked policy from keystone user.
   */
  removePolicy = async (email: string, policyNum: string) => {
    if (!email || !policyNum) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);
    return this.delete(`/api/user/${encodedEmail}/policy/${policyNum}`);
  };

  /**
   * Fetch a user's policy list by email.
   */
  getPoliciesList = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);
    return this.get<KeystonePolicyInfo[]>(
      `/api/user/${encodedEmail}/policyList`,
    );
  };

  /**
   * User-triggered password change, or to initially set a password after using passwordless login.
   */
  resetPassword = async (userId: string, password: string) => {
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }

    const params = new URLSearchParams();
    params.append("userId", userId);
    params.append("password", password);
    params.append("sptoken", "");
    return await this.post<null>(
      `/api/user/password?${params.toString()}`,
      undefined,
      { "Content-Type": "application/x-www-form-urlencoded" },
      true,
    );
  };

  /**
   * Request password reset email
   */
  requestPasswordReset = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const params = new URLSearchParams();
    params.append("email", email);
    return await this.post<null>(
      `/api/user/password/reset?${params.toString()}`,
      undefined,
      undefined,
      true,
    );
  };

  /**
   * Fetch a policy's claim list
   */
  getClaimsList = async (
    policyId: string,
  ): Promise<KeystoneResult<ClaimList>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }

    const encodedPolicyId = encodeURIComponent(policyId);
    const response = await this.get<KeystoneClaimsData>(
      `/api/claims/${encodedPolicyId}?lossFlag=true`,
    );

    if (response instanceof SSError) {
      return response;
    }

    return claimsDataMap(response);
  };

  /**
   * Fetch policy details
   */
  getPolicy = async (
    policyId: string,
  ): Promise<KeystoneResult<PolicyDetail>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const apiRes = await this.get<KeystonePolicyDetail>(
      `/api/policy/${policyId}`,
    );
    if (apiRes instanceof SSError) {
      return apiRes;
    }
    return policyDetailMap(apiRes);
  };

  /**
   * Fetch a policy's claim constants
   */
  getClaimConstants = async (
    policyId: string,
  ): Promise<KeystoneResult<ClaimConstants>> => {
    const apiRes = await this.get<ClaimConstants>(
      `/api/claims/constants/${policyId}`,
    );

    return apiRes;
  };

  /**
   * Fetch a user's UI events
   */
  getUiEvents = async (userId: string): Promise<KeystoneResult<UiEvents>> => {
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    return await this.get<UiEvents>(`/api/ui-events/${userId}`);
  };

  /**
   * Fetch a policy's featured offers
   */
  getPolicyOffers = async (
    keystonePolicyId: string,
    featured?: boolean,
  ): Promise<KeystoneResult<KeystoneOfferEligibility[]>> => {
    if (!keystonePolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const res = await this.get<KeystoneOfferEligibility[]>(
      `/api/offers/${keystonePolicyId}${featured ? "?featured=true" : ""}`,
    );

    return res;
  };

  /**
   * Fetch payment plans for policy along with easypay processing fees
   */
  getPaymentPlans = async (
    policyId: string,
  ): Promise<KeystoneResult<PaymentsPlans>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const apiRes = await this.get<{ data: PaymentsPlans }>(
      `/api/paymentplan/${policyId}`,
    );
    if (isError(apiRes)) {
      return apiRes;
    }
    return apiRes.data;
  };

  /**
   * Fetch a policy's inspections data
   */
  getPolicyInspections = async (
    policyId: string,
    options?: { inspectionsProofUploadFlag?: boolean },
  ): Promise<KeystoneResult<KeystoneInspections>> => {
    // TODO: Add "&groupRoofConditions=TRUE" to paramsString once roof "roll-ups" are supported.
    const paramsString = "uwActionCodeFilter=WRITE_OUT";
    const params = new URLSearchParams(paramsString);
    if (!options?.inspectionsProofUploadFlag) {
      params.append("resolvedFilter", "FALSE");
    }
    let url = `/api/inspection/${policyId}?${params.toString()}`;
    const apiRes = await this.get<KeystoneInspections>(url);
    return apiRes;
  };

  /**
   * Upload files for proof of inspection issue resolution
   */
  uploadInspectionProof = async (
    request: {
      insightPolicyId?: string;
      inspectionId?: string;
      referralId?: string;
      userEmail?: string;
      userFullName?: string;
    },
    files?: File[],
  ): Promise<KeystoneResult<InspectionUploadResponse>> => {
    const formData = new FormData();
    formData.append("request", JSON.stringify(request));
    if (files?.length) {
      for (const file of files) {
        formData.append("file", file);
      }
    }
    return this.post<InspectionUploadResponse>(
      `/api/inspection/upload`,
      formData,
    );
  };

  /**
   * Submit an FNOL (First Notice Of Loss) event.
   */
  fnolChannelSubmit = async (event: unknown) => {
    if (!event) {
      return new MissingArgumentsError(NO_FNOL_EVENT_ERR_MSG);
    }

    return this.post("/api/channel", JSON.stringify(event));
  };

  /**
   * Create an offer for a policy.
   */
  createOffer = async (
    keystonePolicyId: string,
    offerInternalName: string,
  ): Promise<KeystoneResult<KeystoneOfferEligibility>> => {
    if (!keystonePolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    return this.post(
      `/api/offer/${keystonePolicyId}/offering/${offerInternalName}`,
    );
  };

  verifyPayment = async (
    policyId: string,
    zip: string,
  ): Promise<KeystoneResult<KeystonePaymentInfo>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    if (!zip) {
      return new MissingArgumentsError(NO_ZIP_CODE_ERR_MSG);
    }
    const params = new URLSearchParams();
    params.append("policyId", policyId);
    params.append("zipcode", zip);
    return this.post<KeystonePaymentInfo>(
      `/api/paymentinfo/verify?${params.toString()}`,
    );
  };

  enrollEasypay = async (
    paymentInfo: EasyPayFormValues,
    user: { firstName: string; lastName: string; email: string },
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!paymentInfo || !user) {
      return new MissingArgumentsError(NO_PAYMENT_INFO_MSG);
    }
    const formData = new FormData();
    formData.append("paymentFormData", JSON.stringify(paymentInfo));
    formData.append("sessionData", JSON.stringify({ user }));
    return this.post<KeystoneSuccess>(`/api/easypay/enroll`, formData);
  };

  /**
   * Enroll a policy in wind defense program offer (shingles).
   */
  enrollWindDefense = async (
    enrollWindDefenseInfo: WindDefenseEnrollmentValues,
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    return this.post<KeystoneSuccess>(
      `/api/homeservices/order/winddefense/`,
      JSON.stringify(enrollWindDefenseInfo),
      headers,
    );
  };

  /**
   * Enroll a policy in Ting program offer (electrical fire detector).
   */
  enrollTing = async (
    enrollTingInfo: TingEnrollmentValues,
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    return this.post<KeystoneSuccess>(
      `/api/homeservices/order/ting/`,
      JSON.stringify(enrollTingInfo),
      headers,
    );
  };

  getDocumentsToSign = async (
    insightPolicyId: string,
  ): Promise<KeystoneResult<KeystoneDocusignStatus[]>> => {
    if (!insightPolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    return this.get<KeystoneDocusignStatus[]>(
      `/api/docusign/status/${insightPolicyId}`,
    );
  };

  /**
   * Get a signing link for a document by envelope ID
   */
  getDocumentSigningUrl = async (
    envelopeId: string,
  ): Promise<KeystoneResult<{ signingLink: string }>> => {
    if (!envelopeId) {
      return new MissingArgumentsError(NO_ENVELOPE_ID_ERR_MSG);
    }
    return this.get<{ signingLink: string }>(
      `/api/docusign/signinglink/${envelopeId}`,
    );
  };

  /**
   * Update user's account preference key/value store
   */
  updateAccountPreferences = async (
    accountPreferences: { preferenceName: string; preferenceValue: string },
    user: { userId: string; email: string },
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!user?.email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!user?.userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    if (!accountPreferences) {
      return new MissingArgumentsError(NO_ACCOUNT_PREFERENCES_MSG);
    }
    const requestBody = {
      userId: user.userId,
      userEmail: user.email,
      preferenceName: accountPreferences.preferenceName,
      preferenceValue: accountPreferences.preferenceValue,
    };
    return this.post<KeystoneSuccess>(
      `/api/preferences/account`,
      JSON.stringify(requestBody),
      {
        "content-type": "application/json",
      },
    );
  };

  /**
   * Update user's account preference and trigger email notification (e.g. for gift flow).
   * Same request body as updateAccountPreferences; backend sends notification.
   */
  updateAccountPreferencesAndNotify = async (
    accountPreferences: { preferenceName: string; preferenceValue: string },
    user: { userId: string; email: string; firstName: string },
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!user?.email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!user?.userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    if (!user?.firstName) {
      return new MissingArgumentsError(NO_FIRST_NAME_MSG);
    }
    if (!accountPreferences) {
      return new MissingArgumentsError(NO_ACCOUNT_PREFERENCES_MSG);
    }
    const requestBody = {
      userId: user.userId,
      userEmail: user.email,
      userFirstName: user.firstName,
      preferenceName: accountPreferences.preferenceName,
      preferenceValue: accountPreferences.preferenceValue,
    };
    return this.post<KeystoneSuccess>(
      `/api/preferences/account/notify`,
      JSON.stringify(requestBody),
      { "content-type": "application/json" },
    );
  };

  updateMarketingUiEvent = async (
    userId: string,
    currentCount: number,
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }

    const requestBody = {
      marketingOptInPresented: {
        count: currentCount + 1,
        presentedAt: new Date(),
      },
    };
    return this.patch<KeystoneSuccess>(
      `/api/ui-events/${userId}`,
      JSON.stringify(requestBody),
    );
  };

  /**
   * Update policy's property data; roof, trampoline, fire + security alarms, and pool.
   */
  updateProperty = async (
    request: {
      insightPolicyId?: string;
      userEmail?: string;
      propertySection: PropertySection;
      propertyUpdateItems: PropertyUpdateItem[];
    },
    files?: File[],
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    const formData = new FormData();
    formData.append("request", JSON.stringify(request));
    if (files?.length) {
      for (const file of files) {
        formData.append("file", file);
      }
    }
    return this.post<KeystoneSuccess>(`/api/property/update`, formData);
  };

  /**
   * Update property profile notification (pool updates).
   */
  updatePropertyNotification = (
    policyNum: string,
    email: string,
    updates: Record<string, unknown>,
  ): Promise<KeystoneResult<void>> => {
    if (!policyNum) {
      return Promise.resolve(new MissingArgumentsError(NO_POLICY_ID_ERR_MSG));
    }
    const formData = new FormData();
    formData.append("policyId", policyNum);
    formData.append("emailAddress", email);
    formData.append("username", email);
    formData.append("updates", JSON.stringify(updates));
    return this.put<void>(`/api/property/profile/notification`, formData);
  };

  registerUser = (user: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    policyListCsv: string;
    zipCode: string;
  }): Promise<KeystoneResult<KeystoneUser>> => {
    return this.post<KeystoneUser>(`/api/user/register`, JSON.stringify(user), {
      "content-type": "application/json",
    });
  };

  registerLegacyUser = (partialUser: {
    policyList: string;
    firstName: string;
    lastName: string;
    zipCode: string;
    email: string;
    password: string;
    emailVerified: boolean;
  }): Promise<KeystoneResult<void>> => {
    const formData = new FormData();
    formData.append("policyList", partialUser.policyList);
    formData.append("firstName", partialUser.firstName);
    formData.append("lastName", partialUser.lastName);
    formData.append("zipCode", partialUser.zipCode);
    formData.append("email", partialUser.email);
    formData.append("password", partialUser.password);
    formData.append("emailVerified", partialUser.emailVerified.toString());
    return this.post<void>(`/api/user`, formData);
  };

  mortgageeSearch = (searchTerm: string) => {
    const encodedSearchTerm = encodeURIComponent(searchTerm.trim());
    return this.get<KeystoneMortgageeInfo[]>(
      `/api/mortgagee/lookup/${encodedSearchTerm}`,
    );
  };

  updateMortgagee = ({ mortgagee, keystonePolicyId }) => {
    return this.put<null>(
      `/api/mortgagee/${keystonePolicyId}`,
      JSON.stringify(mortgagee),
      {
        "content-type": "application/json",
      },
    );
  };

  sendEmailVerification = (email: string): Promise<KeystoneResult<void>> => {
    const params = new URLSearchParams();
    params.append("emailaddress", email);
    return this.post<void>(`/api/user/reverify?${params.toString()}`);
  };

  /**
   * Update insured's email, phone, or mailing address.
   */
  updateInsuredInfo = (
    policyId: string,
    email: string,
    username: string,
    update: {
      phone?: string;
      email?: string;
      mailingAddress?: {
        streetLine1: string;
        streetLine2?: string;
        city: string;
        state: string;
        zipFirstFive: string;
        mailingAddressUpdateReason: string;
        mailingAddressUpdateDetails: string;
      };
    },
  ): Promise<KeystoneResult<void>> => {
    const updateables: KeystoneInsuredInfoUpdate = {};
    if (update.phone) {
      updateables.phoneNumber = update.phone;
    }
    if (update.email) {
      updateables.emailAddress = update.email;
    }
    if (update.mailingAddress) {
      updateables.mailingAddress = update.mailingAddress;
    }
    const formData = new FormData();
    formData.append("policyId", policyId);
    formData.append("emailAddress", email);
    formData.append("username", username);
    formData.append("updateables", JSON.stringify(updateables));
    return this.put<void>(`/api/property/profile/insuredinfo`, formData);
  };

  /**
   * Enroll a policy in paperless billing.
   */
  enrollPaperlessBilling = (
    policyId: string,
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `/api/paperlessbilling/enroll/${policyId}`,
      undefined,
      {
        "content-type": "application/json",
      },
    );
  };

  /**
   * Update a user's paperless billing email address for a specific policy.
   */
  updatePaperlessBillingEmail = (
    policyId: string,
    email: string,
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `/api/paperlessbilling/billing-email-address/${policyId}`,
      JSON.stringify({ opInsuredEmailAddressBilling: email }),
      { "content-type": "application/json" },
    );
  };

  /**
   * Enroll a policy in paperless documents.
   */
  enrollPaperlessDocuments = (
    policyId: string,
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `/api/paperlessdocuments/enroll/${policyId}`,
      undefined,
      {
        "content-type": "application/json",
      },
    );
  };

  /**
   * Enroll a policy in paperless documents from email.
   */
  enrollPaperlessDocumentsFromEmail = (
    data: string,
  ): Promise<KeystoneResult<void>> => {
    const res = this.patch<void>(
      `/api/paperlessdocuments/data/`,
      data,
      { "content-type": "text/plain" },
      true,
    );
    if (isError(res)) {
      if (res instanceof KeystoneServerError && res.statusCode === 409) {
        res.message = "Policy is already enrolled in paperless documents";
      } else if (res instanceof NotFoundError) {
        res.message =
          "We couldn't find your SageSure insurance policy. Please try again in a few minutes or call us at (800) 481-0661.";
      } else {
        res.message =
          "An error occurred while enrolling in paperless documents";
      }
    }
    return res;
  };

  /**
   * Update a user's paperless billing email address for a specific policy.
   */
  updatePaperlessDocsEmail = (
    policyId: string,
    email: string,
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `/api/paperlessdocuments/documents-email-address/${policyId}`,
      JSON.stringify({ opInsuredEmailAddressDocuments: email }),
      { "content-type": "application/json" },
    );
  };

  /**
   * Get a list of coverages available for a given policy.
   */
  eligibleCoverages = (
    policyNum: string,
  ): Promise<KeystoneResult<CoverageData>> => {
    return this.get<CoverageData>(`/api/coverages/eligible/${policyNum}`);
  };

  /**
   * Get prices for a coverage
   */
  coveragePreview = (
    policyNum: string,
    coverageName: string,
    protectionValue: number,
  ): Promise<KeystoneResult<CoveragePreview>> => {
    const params = new URLSearchParams();
    params.append("coverageName", coverageName);
    params.append("protectionValue", protectionValue.toString());
    return this.get<CoveragePreview>(
      `/api/coverages/preview/${policyNum}?${params.toString()}`,
    );
  };

  /**
   * Purchase additional coverage.
   */
  purchaseCoverage = async ({
    policyNum,
    coverageName,
    protectionValue,
  }: {
    policyNum: string;
    coverageName: string;
    protectionValue: number;
  }): Promise<KeystoneResult<CoverageUpsell>> => {
    const params = new URLSearchParams();
    params.append("policyId", policyNum);
    params.append("coverageName", coverageName);
    params.append("protectionValue", protectionValue.toString());
    return this.post<CoverageUpsell>(
      `/api/coverages/purchase?${params.toString()}`,
    );
  };

  /**
   * Check whether RoofMarketplace can service the given property address.
   */
  checkRoofMarketplaceServiceAvailability = async (address: {
    line1: string;
    city: string;
    state: string;
    zip: string;
  }): Promise<KeystoneResult<{ canService: boolean }>> => {
    return this.post<{ canService: boolean }>(
      `/api/roof-marketplace/service-availability`,
      JSON.stringify({ address }),
      { "Content-Type": "application/json", Accept: "application/json" },
    );
  };

  /**
   * Submit a RoofMarketplace bid/underwriting request via the UI `/api` proxy
   * (rewritten upstream to Keystone `…/roof-marketplace/request/:platformPolicyId`).
   */
  requestRoofMarketplaceBid = async (
    platformPolicyId: string,
    body: RoofMarketplaceBidRequest,
  ): Promise<KeystoneResult<RoofMarketplaceBidResponse>> => {
    if (!platformPolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const encoded = encodeURIComponent(platformPolicyId);
    return this.post<RoofMarketplaceBidResponse>(
      `/api/roof-marketplace/request/${encoded}`,
      JSON.stringify(body),
      { "Content-Type": "application/json", Accept: "application/json" },
    );
  };

  /**
   * Store a RoofMarketplace bid request in the user's UI events.
   */
  updateRoofMarketplaceUiEvent = async (
    userId: string,
    bidId: string,
    policyId: string,
    referralIds: string[],
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    const requestBody = {
      roofMarketplace: {
        bidId,
        policyId,
        referralIds,
      },
    };
    return this.patch<KeystoneSuccess>(
      `/api/ui-events/${userId}`,
      JSON.stringify(requestBody),
    );
  };

  /**
   * Verify user's identity for specific policies
   */
  verifyId = async (
    email: string,
    dob: { month: number; day: number; year: number },
    unlinkedPolicyIds: string[],
  ) => {
    const year = String(dob.year);
    const month = String(dob.month).padStart(2, "0");
    const day = String(dob.day).padStart(2, "0");
    const requestBody = {
      email,
      dateOfBirth: `${year}-${month}-${day}`,
      unlinkedPolicyIds,
    };
    return this.post<KeystonePolicyIdVerification>(
      `/api/policy/verify/identity`,
      JSON.stringify(requestBody),
      { "Content-Type": "application/json", Accept: "application/json" },
    );
  };
}

/**
 * Used in client to parse api responses.
 */
async function parseResponse<T>(
  res: Response,
  empty = false,
): Promise<KeystoneResult<T>> {
  if (res.status === 204 || (res.status === 200 && empty)) {
    return null as T;
  }

  const body = await res.json();
  if (!res.ok) {
    const message = body.message || res.statusText;
    if (res.status === 400) {
      return new BadRequestError(message || "bad request data");
    } else if (res.status === 404) {
      return new NotFoundError(message || "not found");
    } else if (res.status === 401 || res.status === 403) {
      return new UnauthorizedRequestError(message || "unauthorized");
    }
    return new KeystoneServerError(
      message || "internal server error",
      res.status,
    );
  }
  return body as T;
}

/**
 * Internal backend server error.
 */
class KeystoneServerError extends SSError {
  statusCode: number;
  constructor(message: string, statusCode: number = 500) {
    super(message);
    this.name = "KeystoneServerError";
    this.cause = "Internal Keystone API error";
    this.statusCode = statusCode;
  }
}
